import { useState } from 'react'

import { Box, CollapsableCard, RadioButton, Text } from '@factorialco/deprecated-design-system'
import {
  EmployeesEmployeeSelectorInput,
  PerformanceTargetStrategySupportedStrategiesEnum,
} from 'generated/resources/graphql'

import { PerformanceTargetGroupEnum } from 'modules/performance_process/components/review/forms/TargetGroup/targetEnums'

import DynamicFilter from './DynamicFilter'
import { ManualEmployeeFilter } from './ManualEmployeeFilter'
import { Variables } from './targetEnums'
import { targetHelper } from './targetHelper'
import { targetOptions } from './targetOptions'

interface Props {
  variables: Variables
  loading?: boolean
  showEmpty?: boolean
  autoSaveCustomSelection?: boolean
  onChange: (values: Variables) => void
  onSubmit?: () => void
}

const selectedTargetFromStrategy = (
  strategy?: PerformanceTargetStrategySupportedStrategiesEnum
): PerformanceTargetGroupEnum | null => {
  switch (strategy) {
    case 'all_employees':
      return PerformanceTargetGroupEnum.AllEmployees
    case 'by_locations':
    case 'by_teams':
      return PerformanceTargetGroupEnum.DynamicSelection
    case 'manual_selection':
    case 'by_employees':
      return PerformanceTargetGroupEnum.ByEmployees
    default:
      return null
  }
}

export const TargetGroupSection = ({
  autoSaveCustomSelection = false,
  onChange,
  variables,
  loading = false,
  showEmpty = false,
  onSubmit,
}: Props) => {
  const [selectedTarget, setSelectedTarget] = useState(
    variables.targetStrategy ? selectedTargetFromStrategy(variables.targetStrategy) : null
  )

  const [primaryFilter, setPrimaryFilter] = useState<
    PerformanceTargetStrategySupportedStrategiesEnum | undefined | null
  >(targetHelper(variables.targetStrategy) ? variables.targetStrategy : undefined)

  const [secondaryFilter, setSecondaryFilter] = useState<number[]>(
    targetHelper(variables.targetStrategy) ? (variables.arguments ?? []) : []
  )

  const handleChange = ({
    selectedTarget: target,
    arguments: args,
    targetEmployeeSelection,
  }: {
    selectedTarget:
      | PerformanceTargetGroupEnum
      | PerformanceTargetStrategySupportedStrategiesEnum
      | null
    arguments: number[] | null
    targetEmployeeSelection?: EmployeesEmployeeSelectorInput | null
  }) => {
    switch (target) {
      case PerformanceTargetGroupEnum.AllEmployees:
        onChange({
          targetStrategy: 'all_employees',
          arguments: null,
          targetEmployeeSelection: undefined,
        })
        onSubmit?.()
        break
      case PerformanceTargetGroupEnum.ManualSelection:
        onChange({
          targetStrategy: 'manual_selection',
          arguments: args,
          targetEmployeeSelection: undefined,
        })
        break
      case PerformanceTargetGroupEnum.ByEmployees:
        onChange({
          targetStrategy: 'by_employees',
          arguments: args ?? [],
          targetEmployeeSelection: targetEmployeeSelection ?? undefined,
        })
        onSubmit?.()
        break
      case 'by_locations':
      case 'by_teams':
        onChange({
          targetStrategy: target,
          arguments: args,
          targetEmployeeSelection: undefined,
        })
        break
      case null:
        onChange({
          targetStrategy: null,
          arguments: null,
          targetEmployeeSelection: undefined,
        })
    }
  }

  const visibleTargetOptions = showEmpty
    ? targetOptions()
    : targetOptions().filter((option) => option.value !== null)

  const handleSelectTarget = (value: PerformanceTargetGroupEnum | null) => {
    setSelectedTarget(value)
    setSecondaryFilter([])
    handleChange({ selectedTarget: value, arguments: null, targetEmployeeSelection: null })
  }

  return (
    <Box gap="s8" minWidth="s400">
      {visibleTargetOptions.map((target) => (
        <CollapsableCard
          key={target.value}
          open={selectedTarget === target.value && target.hasOptions === true}
          Card={
            <Box onClick={() => handleSelectTarget(target.value)}>
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">{target.label}</Text.Normal>

                <RadioButton
                  disabled={loading}
                  size="medium"
                  value={target.value || 'none'}
                  onChange={() => null}
                  checked={selectedTarget === target.value}
                />
              </Box>
              {target.description && <Text.Normal>{target.description}</Text.Normal>}
            </Box>
          }
          Content={
            <>
              {selectedTarget === PerformanceTargetGroupEnum.DynamicSelection && (
                <DynamicFilter
                  primaryFilter={primaryFilter}
                  setPrimaryFilter={(filter) => {
                    if (!filter) return
                    handleChange({
                      selectedTarget: filter,
                      arguments: [],
                    })
                    setPrimaryFilter(filter)
                    setSecondaryFilter([])
                  }}
                  secondaryFilter={secondaryFilter}
                  setSecondaryFilter={(value) => {
                    if (!primaryFilter) return
                    setSecondaryFilter(value)
                    handleChange({
                      selectedTarget: primaryFilter,
                      arguments: value ?? [],
                    })
                  }}
                  onSubmit={onSubmit ? onSubmit : () => null}
                />
              )}

              {selectedTarget === PerformanceTargetGroupEnum.ByEmployees && (
                <ManualEmployeeFilter
                  autoSaveCustomSelection={autoSaveCustomSelection}
                  variables={variables}
                  onSelectEmployees={(selection) => {
                    const employeeIds = selection.type === 'manual' ? (selection.ids ?? null) : null
                    handleChange({
                      selectedTarget: PerformanceTargetGroupEnum.ByEmployees,
                      arguments: employeeIds,
                      targetEmployeeSelection: selection,
                    })
                  }}
                />
              )}
            </>
          }
        />
      ))}
    </Box>
  )
}
